import { useMemo } from 'react';
import {
  useCountryConfig,
  resolveCountryConfig,
  type CountryConfig,
} from '@/lib/countryConfig';

/**
 * Multi-market phone helpers, kept OUT of the PhoneInput component file so
 * both the input and its validators can be imported without tripping
 * react-refresh/only-export-components.
 */

/** Longest prefix wins, so a shorter prefix never shadows a longer one. */
export function splitE164(
  e164: string,
  configs: CountryConfig[],
): { config: CountryConfig | null; national: string } {
  const trimmed = (e164 || '').replace(/[^\d+]/g, '');
  if (!trimmed.startsWith('+')) return { config: null, national: trimmed.replace(/^0+/, '') };

  const match = [...configs]
    .sort((a, b) => b.phonePrefix.length - a.phonePrefix.length)
    .find((c) => trimmed.startsWith(c.phonePrefix));

  return match
    ? { config: match, national: trimmed.slice(match.phonePrefix.length) }
    : { config: null, national: '' };
}

/** True when the national part has the digit count this country expects. */
export function isValidNationalNumber(national: string, config: CountryConfig): boolean {
  return new RegExp(`^[0-9]{${config.phoneNationalDigits}}$`).test(national);
}

/** Validate a full E.164 value against the operational countries given. */
export function isValidPhone(e164: string, configs: CountryConfig[]): boolean {
  const { config, national } = splitE164(e164, configs);
  return !!config && isValidNationalNumber(national, config);
}

/**
 * Operational countries as phone configs, de-duplicated by dialling prefix —
 * two markets sharing a prefix would otherwise render as indistinguishable
 * options. Shared so validators outside PhoneInput accept EVERY country the
 * picker offers, not just the viewer's active market (a booking with a
 * non-default prefix was previously unsubmittable).
 */
export function usePhoneCountryConfigs(): CountryConfig[] {
  const { config: activeConfig, countries } = useCountryConfig();
  return useMemo(() => {
    const resolved = (countries ?? []).map((c) => resolveCountryConfig(c));
    const list = resolved.length > 0 ? resolved : [activeConfig];
    const seen = new Set<string>();
    return list.filter((c) => {
      if (seen.has(c.phonePrefix)) return false;
      seen.add(c.phonePrefix);
      return true;
    });
  }, [countries, activeConfig]);
}
